const KLAVIYO_REVISION = '2024-06-15';
const KLAVIYO_CLIENT_SUBSCRIPTION_REVISION = '2024-10-15';

function getConfig() {
  return window.klaviyoBackInStockConfig || null;
}

function getKlaviyoHeaders(revision = KLAVIYO_REVISION) {
  return {
    'Content-Type': 'application/json',
    revision,
  };
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function buildShopifyVariantId(variantId) {
  return `$shopify:::$default:::${variantId}`;
}

async function parseKlaviyoResponse(response) {
  const text = await response.text();
  if (!text) return { data: null };

  try {
    return { data: JSON.parse(text) };
  } catch {
    return { data: null };
  }
}

function getErrorDetail(errors, fallback) {
  return errors?.[0]?.detail || fallback;
}

async function subscribeToBackInStock({ accountId, email, variantId }) {
  const payload = {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        channels: ['EMAIL'],
        profile: {
          data: {
            type: 'profile',
            attributes: { email },
          },
        },
      },
      relationships: {
        variant: {
          data: {
            type: 'catalog-variant',
            id: buildShopifyVariantId(variantId),
          },
        },
      },
    },
  };

  const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(accountId)}`, {
    method: 'POST',
    headers: getKlaviyoHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.status === 202) {
    return { success: true };
  }

  const { data } = await parseKlaviyoResponse(response);
  const firstError = data?.errors?.[0];

  return {
    success: false,
    error: getErrorDetail(data?.errors),
    code: firstError?.code,
  };
}

function isVariantNotFoundError(result) {
  const error = result?.error?.toLowerCase() || '';

  return result?.code === 'variant_not_found' || (error.includes('variant') && error.includes('does not exist'));
}

async function subscribeToBackInStockWithFallback({ accountId, email, variantIds }) {
  const uniqueIds = [...new Set(variantIds.filter(Boolean).map(String))];
  let lastResult = { success: false };

  for (const variantId of uniqueIds) {
    const result = await subscribeToBackInStock({ accountId, email, variantId });
    if (result.success) return result;

    lastResult = result;
    if (!isVariantNotFoundError(result)) break;
  }

  return lastResult;
}

function normalizeListId(listId) {
  const id = String(listId || '').trim();
  return id || null;
}

function buildListSubscribePayload(email, listId) {
  return {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Back in stock',
        profile: {
          data: {
            type: 'profile',
            attributes: { email },
          },
        },
      },
      relationships: {
        list: {
          data: {
            type: 'list',
            id: listId,
          },
        },
      },
    },
  };
}

async function subscribeToList({ accountId, email, listId }) {
  const normalizedListId = normalizeListId(listId);

  if (!normalizedListId) {
    return { success: true, skipped: true };
  }

  const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(accountId)}`, {
    method: 'POST',
    headers: getKlaviyoHeaders(KLAVIYO_CLIENT_SUBSCRIPTION_REVISION),
    body: JSON.stringify(buildListSubscribePayload(email, normalizedListId)),
  });

  if (response.status === 202) {
    return { success: true };
  }

  const { data } = await parseKlaviyoResponse(response);
  const error = getErrorDetail(data?.errors);

  return { success: false, error };
}

class BackInStockForm extends HTMLElement {
  connectedCallback() {
    this.config = getConfig();
    if (!this.config?.accountId) return;

    this.fields = this.querySelector('.product__back-in-stock__fields');
    this.emailInput = this.querySelector('[data-bis-email]');
    this.submitBtn = this.querySelector('[data-bis-submit]');
    this.variantInput = this.querySelector('[data-bis-variant-id]');
    this.newsletterCheckbox = this.querySelector('[data-bis-newsletter]');
    this.confirmation = this.querySelector('[data-bis-confirmation]');
    this.errorMessage = this.querySelector('[data-bis-error]');
    this.emailError = this.querySelector('[data-bis-email-error]');
    this.defaultErrorText = this.errorMessage?.textContent?.trim() || '';
    this.isSubmitting = false;

    this.onSubmit = this.onSubmit.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onProductSelectChange = this.onProductSelectChange.bind(this);
    this.onEmailInput = this.onEmailInput.bind(this);
    this.stopParentFormSubmit = this.stopParentFormSubmit.bind(this);

    this.productSection =
      this.closest('[data-section-type="product-template"]') ||
      this.closest('.shopify-section--product');

    this.submitBtn?.addEventListener('click', this.onSubmit);
    this.emailInput?.addEventListener('input', this.onEmailInput);
    this.emailInput?.addEventListener('change', this.onEmailInput);
    this.updateSubmitState();

    this.emailInput?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      event.stopPropagation();
      if (!this.submitBtn?.disabled) this.onSubmit(event);
    });

    this.productForm = this.closest('form[action*="/cart/add"]');
    const productSelect = this.productForm?.querySelector('[data-product-select]');
    this.lastSelectorVariantId = String(productSelect?.value || this.variantInput?.value || '');

    this.productForm?.addEventListener('submit', this.stopParentFormSubmit);
    this.productForm?.addEventListener('change', this.onProductSelectChange);

    this.productSection?.addEventListener('theme:variant:change', this.onVariantChange);
    document.addEventListener('theme:variant:change', this.onVariantChange);
  }

  disconnectedCallback() {
    this.submitBtn?.removeEventListener('click', this.onSubmit);
    this.emailInput?.removeEventListener('input', this.onEmailInput);
    this.emailInput?.removeEventListener('change', this.onEmailInput);

    this.productForm?.removeEventListener('submit', this.stopParentFormSubmit);
    this.productForm?.removeEventListener('change', this.onProductSelectChange);

    this.productSection?.removeEventListener('theme:variant:change', this.onVariantChange);
    document.removeEventListener('theme:variant:change', this.onVariantChange);
  }

  stopParentFormSubmit(event) {
    if (!this.contains(document.activeElement)) return;
    event.preventDefault();
    event.stopPropagation();
  }

  getVariantIdsToTry() {
    const ids = [];

    if (this.dataset.pageVariantId) {
      ids.push(this.dataset.pageVariantId);
    }

    if (this.cartVariantId) {
      ids.push(this.cartVariantId);
    }

    const currentId = this.variantInput?.value;
    if (currentId) {
      ids.push(currentId);
    }

    return [...new Set(ids.map(String))];
  }

  isEventForThisProduct(event) {
    if (!this.productSection) return true;

    const target = event.target;
    if (!target) return false;

    return target === this.productSection || this.productSection.contains(target);
  }

  updateActiveVariant(variant) {
    if (!variant?.id) return;

    const variantId = String(variant.id);
    this.cartVariantId = variantId;

    if (this.dataset.pageVariantId) {
      if (this.variantInput) this.variantInput.value = this.dataset.pageVariantId;
    } else if (this.variantInput) {
      this.variantInput.value = variantId;
    }
  }

  onVariantChange(event) {
    if (!this.isEventForThisProduct(event)) return;

    const variant = event.detail?.variant;
    if (!variant?.id) return;

    const nextVariantId = String(variant.id);
    if (nextVariantId === this.lastSelectorVariantId) return;

    this.lastSelectorVariantId = nextVariantId;
    this.updateActiveVariant(variant);
    this.resetForm();
  }

  onProductSelectChange(event) {
    const select = event.target?.matches?.('[data-product-select]')
      ? event.target
      : event.target?.closest?.('[data-product-select]');

    if (!select || !this.productForm?.contains(select)) return;

    const nextVariantId = String(select.value || '');
    if (!nextVariantId || nextVariantId === this.lastSelectorVariantId) return;

    this.lastSelectorVariantId = nextVariantId;
    this.updateActiveVariant({ id: nextVariantId });
    this.resetForm();
  }

  updateSubmitState() {
    if (!this.submitBtn) return;

    if (this.isSubmitting) {
      this.submitBtn.disabled = true;
      return;
    }

    const email = this.emailInput?.value?.trim() || '';
    this.submitBtn.disabled = !isValidEmail(email);
  }

  showEmailError() {
    this.emailError?.removeAttribute('hidden');
    this.emailInput?.setAttribute('aria-invalid', 'true');
  }

  hideEmailError() {
    this.emailError?.setAttribute('hidden', '');
    this.emailInput?.removeAttribute('aria-invalid');
  }

  onEmailInput() {
    this.hideEmailError();
    this.updateSubmitState();
  }

  resetForm() {
    this.classList.remove('is-success');
    this.confirmation?.setAttribute('hidden', '');
    this.errorMessage?.setAttribute('hidden', '');
    this.hideEmailError();
    this.setLoading(false);
    if (this.errorMessage) {
      this.errorMessage.textContent = this.defaultErrorText;
    }
  }

  resetMessages() {
    this.resetForm();
  }

  setLoading(loading) {
    this.submitBtn?.classList.toggle('is-loading', loading);
    this.isSubmitting = loading;
    this.updateSubmitState();
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message || this.defaultErrorText;
      this.errorMessage.removeAttribute('hidden');
    }
    this.confirmation?.setAttribute('hidden', '');
  }

  showSuccess() {
    this.classList.add('is-success');
    this.confirmation?.removeAttribute('hidden');
    this.errorMessage?.setAttribute('hidden', '');
  }

  async onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    const config = this.config || getConfig();
    if (!config?.accountId || !this.emailInput || !this.variantInput) return;

    const email = this.emailInput.value.trim();
    const variantId = this.variantInput.value;

    if (!email || !isValidEmail(email)) {
      this.showEmailError();
      this.emailInput?.focus();
      return;
    }

    this.resetMessages();
    this.setLoading(true);

    try {
      const variantIds = this.getVariantIdsToTry();
      const bisResult = await subscribeToBackInStockWithFallback({
        accountId: config.accountId,
        email,
        variantIds: variantIds.length ? variantIds : [variantId],
      });

      if (!bisResult.success) {
        this.showError(bisResult.error);
        return;
      }

      this.hideEmailError();

      if (this.newsletterCheckbox?.checked) {
        const listResult = await subscribeToList({
          accountId: config.accountId,
          email,
          listId: config.listId,
        });

        if (!listResult.success && !listResult.skipped) {
          console.warn('[back-in-stock] Newsletter list subscribe failed:', listResult.error, {
            listId: normalizeListId(config.listId),
            accountId: config.accountId,
          });
        }
      }

      this.showSuccess();
    } catch {
      this.showError();
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
